/*! Copyright (c) Safe As Milk. All rights reserved. */
import DetailsDisclosure from "details-disclosure";

class HeaderDetailsDisclosure extends DetailsDisclosure {
    #boundMouseEnterListener;
    #boundMouseLeaveListener;
    #boundContentEnterListener;
    #boundMouseMoveListener;
    #summaryLink;
    #lastMouseY = 0;
    #mouseMovingDown = false;
    constructor() {
        super();
        this.#boundMouseEnterListener = this.mouseEnterListener.bind(this);
        this.#boundMouseLeaveListener = this.mouseLeaveListener.bind(this);
        this.#boundContentEnterListener = this.contentEnterListener.bind(this);
        this.#boundMouseMoveListener = this.mouseMoveListener.bind(this);
    }
    connectedCallback() {
        this.#summaryLink = this.querySelector("summary > a");
        this.summary = this.querySelector("summary");
        if (!this.mainDetailsToggle.hasAttribute("open")) this.#disableSummaryLink();
        // mouseenter on summary triggers open and closes others
        this.summary.addEventListener("mouseenter", this.#boundMouseEnterListener);
        // mouseenter on nav__sub-wrap (has pointer-events: auto) cancels close timeout
        this.contentWrap = this.content.querySelector('.nav__sub-wrap') || this.content;
        this.contentWrap.addEventListener("mouseenter", this.#boundContentEnterListener);
        // mouseleave on summary starts potential close
        this.summary.addEventListener("mouseleave", this.#boundMouseLeaveListener);
        // track mouse direction
        this.addEventListener("mousemove", this.#boundMouseMoveListener);
        // track document mouse position so we can check gap area in timeout
        this._docMouseMoveListener = (e) => { this._docMouseX = e.clientX; this._docMouseY = e.clientY; };
        document.addEventListener('mousemove', this._docMouseMoveListener);
        // click outside closes the menu
        this._clickOutsideListener = (e) => {
            if (!this.mainDetailsToggle.hasAttribute('open')) return;
            if (!this.contains(e.target)) {
                clearTimeout(this._openTimeout);
                clearTimeout(this._closeTimeout);
                this.close();
            }
        };
        document.addEventListener('click', this._clickOutsideListener);
        // inject close button into nav__sub-wrap if this is a mega menu
        if (this.classList.contains('mega') && this.contentWrap && !this.contentWrap.querySelector('.nav__sub-close')) {
            const btn = document.createElement('button');
            btn.className = 'nav__sub-close';
            btn.setAttribute('aria-label', 'Sluit menu');
            btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
            btn.addEventListener('click', () => {
                clearTimeout(this._openTimeout);
                clearTimeout(this._closeTimeout);
                this.close();
            });
            this.contentWrap.prepend(btn);
        }
        // swipe up on the dropdown to close
        if (this.classList.contains('mega') && this.contentWrap) {
            let touchStartY = null;
            this._touchStartListener = (e) => { touchStartY = e.touches[0].clientY; };
            this._touchEndListener = (e) => {
                if (touchStartY === null) return;
                const deltaY = e.changedTouches[0].clientY - touchStartY;
                if (deltaY < -40) { // swipe up >= 40px
                    clearTimeout(this._openTimeout);
                    clearTimeout(this._closeTimeout);
                    this.close();
                }
                touchStartY = null;
            };
            this.contentWrap.addEventListener('touchstart', this._touchStartListener, { passive: true });
            this.contentWrap.addEventListener('touchend', this._touchEndListener, { passive: true });
        }
    }
    disconnectedCallback() {
        this.disableListeners();
    }
    open() {
        if (this.mainDetailsToggle.hasAttribute("open")) return;
        this.mainDetailsToggle.setAttribute("open", "");
        this.querySelector("summary").setAttribute("aria-expanded", true);
        if (this.content.getBoundingClientRect().x + this.content.offsetWidth > window.innerWidth - 30) {
            this.content.classList.add("is-left-aligned");
        }
        if (this.hasAttribute("adjust-mega-menu-height")) this.#setContentHeight(36);
    }
    close() {
        if (!this.mainDetailsToggle.hasAttribute("open")) return;
        this.mainDetailsToggle.removeAttribute("open");
        this.mainDetailsToggle.querySelector("summary").setAttribute("aria-expanded", false);
        this.content.classList.remove("is-left-aligned");
        if (this.hasAttribute("adjust-mega-menu-height")) this.#removeContentHeight();
    }
    onToggle() {
        if (!this.panelAnimations) this.panelAnimations = this.content.getAnimations();
        if (!this.contentAnimations) {
            this.contentAnimations = Array.from(this.querySelectorAll(".has-animation")).reduce(((animations, element) => {
                const animation = element.getAnimations();
                return [ ...animations, ...animation ];
            }), []);
        }
        if (this.mainDetailsToggle.hasAttribute("open")) {
            setTimeout((() => {
                this.#enableSummaryLink();
            }), 50);
            this.panelAnimations.forEach((animation => animation.play()));
            this.contentAnimations.forEach((animation => animation.play()));
            document.body.setAttribute("header-menu-open", "");
            if (this.classList.contains("mega")) {
                document.body.setAttribute("header-mega-menu-open", "");
            }
        } else {
            this.#disableSummaryLink();
            this.panelAnimations.forEach((animation => animation.cancel()));
            this.contentAnimations.forEach((animation => animation.cancel()));
            document.body.removeAttribute("header-menu-open", "");
            if (this.classList.contains("mega")) {
                document.body.removeAttribute("header-mega-menu-open", "");
            }
        }
    }
    mouseEnterListener() {
        if (document.querySelector("body").hasAttribute("header-details-disclosure-edit")) return;
        // Cancel any pending close for this menu
        clearTimeout(this._closeTimeout);
        // Small delay before opening so diagonal mouse movement doesn't trigger other menus
        clearTimeout(this._openTimeout);
        this._openTimeout = setTimeout(() => {
            // Close all other open menus
            document.querySelectorAll('header-details-disclosure').forEach(disclosure => {
                if (disclosure !== this && disclosure.mainDetailsToggle.hasAttribute('open')) {
                    clearTimeout(disclosure._closeTimeout);
                    clearTimeout(disclosure._openTimeout);
                    disclosure.close();
                }
            });
            this.open();
        }, 150);
    }
    contentEnterListener() {
        // Mouse entered dropdown content, cancel the close timeout
        clearTimeout(this._closeTimeout);
    }
    mouseMoveListener(e) {
        // Track if mouse is moving downward (toward dropdown)
        this.#mouseMovingDown = e.clientY > this.#lastMouseY;
        this.#lastMouseY = e.clientY;
    }
    mouseLeaveListener(e) {
        if (document.querySelector("body").hasAttribute("header-details-disclosure-edit")) return;
        // Cancel any pending open
        clearTimeout(this._openTimeout);
        // Don't close if mouse moved to cart modal/popup
        if (e && e.relatedTarget && e.relatedTarget.closest('.modal--cart, .popup--cart-notification')) return;
        // Don't close if mouse moved to our own dropdown content
        if (e && e.relatedTarget && (this.content.contains(e.relatedTarget) || e.relatedTarget === this.content)) {
            return;
        }
        // If moving down (toward dropdown), give more time
        const timeout = this.#mouseMovingDown ? 350 : 100;
        clearTimeout(this._closeTimeout);
        this._closeTimeout = setTimeout(() => {
            // Check if mouse is in the actual content (pointer-events:auto → :hover works here)
            if (this.contentWrap && this.contentWrap.matches(':hover')) return;
            // Check mouse coordinates: is mouse in the gap between summary and contentWrap?
            const mx = this._docMouseX;
            const my = this._docMouseY;
            if (mx !== undefined && my !== undefined && this.contentWrap) {
                const wrapRect = this.contentWrap.getBoundingClientRect();
                const summaryRect = this.summary.getBoundingClientRect();
                // In the vertical corridor between summary bottom and contentWrap top
                if (mx >= wrapRect.left && mx <= wrapRect.right &&
                    my >= summaryRect.bottom && my <= wrapRect.bottom) return;
            }
            this.close();
        }, timeout);
    }
    disableListeners() {
        this.summary.removeEventListener("mouseenter", this.#boundMouseEnterListener);
        this.contentWrap.removeEventListener("mouseenter", this.#boundContentEnterListener);
        this.summary.removeEventListener("mouseleave", this.#boundMouseLeaveListener);
        this.removeEventListener("mousemove", this.#boundMouseMoveListener);
        if (this._docMouseMoveListener) document.removeEventListener('mousemove', this._docMouseMoveListener);
        if (this._clickOutsideListener) document.removeEventListener('click', this._clickOutsideListener);
        if (this._touchStartListener && this.contentWrap) this.contentWrap.removeEventListener('touchstart', this._touchStartListener);
        if (this._touchEndListener && this.contentWrap) this.contentWrap.removeEventListener('touchend', this._touchEndListener);
    }
    #setContentHeight(padding = 0) {
        const headerSection = document.querySelector(".js-header");
        const headerBottomBoundary = headerSection && headerSection.getBoundingClientRect().bottom > 0 ? headerSection.getBoundingClientRect().bottom : 0;
        const combinedPanelCutoff = Math.round(headerBottomBoundary + padding);
        if (this.content.offsetHeight > window.innerHeight - combinedPanelCutoff) {
            this.content.style.setProperty("--header-elements-height", `${combinedPanelCutoff}px`);
            this.content.classList.add("has-height-control");
        }
    }
    #removeContentHeight() {
        this.content.classList.remove("has-height-control");
    }
    #enableSummaryLink() {
        if (this.#summaryLink) {
            this.#summaryLink.removeEventListener("click", HeaderDetailsDisclosure.#summaryLinkListener);
        }
    }
    #disableSummaryLink() {
        if (this.#summaryLink) {
            this.#summaryLink.addEventListener("click", HeaderDetailsDisclosure.#summaryLinkListener);
        }
    }
    static #summaryLinkListener(e) {
        e.preventDefault();
    }
}

customElements.define("header-details-disclosure", HeaderDetailsDisclosure);
//# sourceMappingURL=header-details-disclosure.js.map
